import {
  Alert,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  UncontrolledDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
} from "reactstrap";
import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import PageHeader from "../../../common/PageHeader";
import FalconCardHeader from "../../../common/FalconCardHeader";
import withRedirect from "../../../../helpers/hoc/withRedirect";
import ButtonIcon from "../../../common/ButtonIcon";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import { fetchPropertyPaymentOwners } from "../../../../helpers/api/finance";
import { isIterableArray } from "../../../../helpers/utils";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { PreloadState } from "../../../UI/preloading/PreloadState";
import Datetime from "react-datetime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SupportLink from "../../support/components/SupportLink";
import Avatar from "../../../common/Avatar";
import useSWRFetchPagination from "../../../../helpers/hooks/useSWRFetchPagination";
import FullPagination from "../../../common/pagination/FullPagination";

const IndicatorsPaymentsProperties = ({ showPagination = true }) => {
  const { id } = useParams();
  const history = useHistory();

  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { role } = useSelector((state) => state.auth.user);
  const [formDate, setformDate] = useState({ since: null, to: null });
  const [search, setSearch] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [params, setParams] = useState({
    condominiumId: Number(id) || selectedCondominium,
    search: null,
    start_date: null,
    end_date: null,
  });

  const {
    data,
    isLoading,
    meta,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchPropertyPaymentOwners, params);

  const applyFilters = (nextSearch = search, nextDates = formDate) => {
    const term = typeof nextSearch === "string" ? nextSearch.trim() : "";
    setSearch(term);
    setParams((prev) => ({
      ...prev,
      search: term || null,
      start_date: nextDates.since ? nextDates.since.format("YYYY-MM-DD") : null,
      end_date: nextDates.to ? nextDates.to.format("YYYY-MM-DD") : null,
    }));
  };

  return (
    <Fragment>
      <PageHeader
        title="Pagos de propietarios"
        titleTag="h2"
        description={"Visualiza los pagos de los propietarios del condominio."}
        className="mb-3"
        col={{ lg: 9 }}
      >
        {role === "manager" && <SupportLink />}
      </PageHeader>
      <Fragment>
        <Card>
          <FalconCardHeader title="Pagos realizados" />
          <CardBody className="border-bottom pt-0 pb-3">
            <Row className={`align-items-center ${showDateFilter ? "mb-3" : ""}`}>
              <Col xs="auto" className="d-flex align-items-center">
                <Label className="mb-0 mr-2 text-600">Filtrar</Label>
              </Col>
              <Col xs={12} md>
                <Input
                  type="text"
                  placeholder="Inmueble, nombre, usuario, cédula, teléfono o correo"
                  value={search}
                  onChange={({ target }) => {
                    setSearch(target.value);
                    if (target.value.trim() === "") applyFilters("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    applyFilters(e.currentTarget.value);
                  }}
                />
              </Col>
              <Col xs="auto" className="d-flex align-items-center pl-4">
                <Label
                  style={{ cursor: "pointer" }}
                  htmlFor="filterByDate"
                  className="mb-0"
                >
                  ¿Filtrar por fecha?
                </Label>
                <Input
                  id="filterByDate"
                  type="checkbox"
                  style={{ cursor: "pointer" }}
                  className="mt-0 position-static ml-2"
                  checked={showDateFilter}
                  onChange={({ target }) => {
                    setShowDateFilter(target.checked);
                    if (!target.checked) {
                      const cleared = { since: null, to: null };
                      setformDate(cleared);
                      applyFilters(search, cleared);
                    }
                  }}
                />
              </Col>
              {!showDateFilter && (
                <Col xs="auto">
                  <ButtonIcon
                    className="text-nowrap"
                    icon={faSearch}
                    color="falcon-default"
                    onClick={() => applyFilters()}
                  >
                    Buscar
                  </ButtonIcon>
                </Col>
              )}
            </Row>
            {showDateFilter && (
              <Row className="justify-content-center justify-content-md-start align-items-center">
                <Col md={4} sm={12} className="my-2 my-md-0">
                  <Datetime
                    value={formDate.since}
                    isValidDate={(current) => !current.isAfter()}
                    onChange={(d) =>
                      setformDate((prev) => ({ ...prev, since: d }))
                    }
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    inputProps={{ placeholder: "DD/MM/AAAA" }}
                  />
                </Col>
                <Col xs="auto">
                  <b>a</b>
                </Col>
                <Col md={4} sm={12} className="my-2 my-md-0">
                  <Datetime
                    value={formDate.to}
                    isValidDate={(current) =>
                      (!formDate.since || current.isAfter(formDate.since)) && !current.isAfter()
                    }
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    inputProps={{ placeholder: "DD/MM/AAAA" }}
                    onChange={(d) =>
                      setformDate((prev) => ({ ...prev, to: d }))
                    }
                  />
                </Col>
                <Col xs="auto" className="my-2 my-md-0">
                  <ButtonIcon
                    className="text-nowrap"
                    icon={faSearch}
                    color="falcon-default"
                    onClick={() => applyFilters()}
                  >
                    Buscar
                  </ButtonIcon>
                </Col>
              </Row>
            )}
          </CardBody>
          <CardBody className="fs--1 p-0">
            <PreloadState cond={isIterableArray(data)} loading={isLoading}>
              {data.length > 0 ? (
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th className="w-auto align-middle"></th>
                      <th className="text-center align-middle">DNI</th>
                      <th width="35%" className="align-middle">
                        Usuario y nombre
                      </th>
                      <th className="align-middle">N° de teléfono</th>
                      <th width="10%" className="text-center align-middle">
                        Opciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((property) => (
                      <tr key={property.id}>
                        <td className="w-auto text-center">
                          <Avatar src={undefined} />
                        </td>
                        <td className="align-middle text-center">
                          {property?.user?.data?.dni
                            ? property?.user?.data?.dni
                            : "Documento no registrado"}
                        </td>
                        <td className="align-middle">
                          <b>{property?.user?.username}</b> |{" "}
                          {`${property?.user?.data?.firstname} ${property?.user?.data?.lastname}`}
                        </td>
                        <td className="align-middle">
                          {property?.user?.data?.phone
                            ? property?.user?.data?.phone
                            : "Télefono no registrado"}
                        </td>
                        <td className="text-center align-middle">
                          <UncontrolledDropdown
                            direction="left"
                            className="d-inline-block ml-2"
                          >
                            <DropdownToggle color="falcon-default" size="sm">
                              <FontAwesomeIcon icon="ellipsis-h" />
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                onClick={() =>
                                  history.push({
                                    pathname: `/root/indicators/${id}/payments/${property.id}`,
                                  })
                                }
                              >
                                Ver detalles
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Row className="p-card">
                  <Col>
                    <Alert color="info" className="mb-0">
                      {params.search || params.start_date ? (
                        <>
                          No hay propietarios con pagos que coincidan con los
                          filtros.
                        </>
                      ) : (
                        <>
                          No se han realizado <b>pagos</b> por parte de
                          propietarios <b>actualmente</b>.
                        </>
                      )}
                    </Alert>
                  </Col>
                </Row>
              )}
              {showPagination && (
                <FullPagination
                  nextPage={nextPage}
                  prevPage={previousPage}
                  goToPage={goToPage}
                  meta={meta}
                  className={"shadow-none"}
                  posText={"Propietarios"}
                  perPage={perPage}
                />
              )}
            </PreloadState>
          </CardBody>
        </Card>
      </Fragment>
    </Fragment>
  );
};

IndicatorsPaymentsProperties.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(IndicatorsPaymentsProperties);
